import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBlock } from './Spinner';
import { api } from '../api/client';
import type { Account } from '../api/client';
import { Select, Label, ListBox } from '@heroui/react';
import type { Key } from 'react-aria-components';
import styles from './CopyConfigModal.module.css';

type CampaignType = 'mass' | 'priority';

interface CopyCampaignModalProps {
  type: CampaignType;
  currentAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface CampaignItem {
  id: number;
  name: string;
  is_active: boolean;
}

export function CopyCampaignModal({ type, currentAccountId, isOpen, onClose, onComplete }: CopyCampaignModalProps) {
  const { t } = useTranslation('account');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sourceAccountId, setSourceAccountId] = useState<string>('');
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [copyResult, setCopyResult] = useState<{ campaigns: number; lists: number; messages: number } | null>(null);

  // Get modal title based on type
  const getTitle = () => {
    return type === 'mass'
      ? t('copyCampaign.copyMassMessageCampaigns')
      : t('copyCampaign.copyPriorityMassMessageCampaigns');
  };

  // Load accounts on open
  useEffect(() => {
    if (isOpen) {
      loadAccounts();
      setSourceAccountId('');
      setCampaigns([]);
      setSelectedCampaigns(new Set());
      setError(null);
      setSuccess(false);
      setCopyResult(null);
    }
  }, [isOpen]);

  // Load campaigns when source account changes
  useEffect(() => {
    if (sourceAccountId) {
      loadCampaigns();
    }
  }, [sourceAccountId, type]);

  const loadAccounts = async () => {
    try {
      const allAccounts = await api.getAccounts();
      // Filter out current account
      const otherAccounts = allAccounts.filter(acc => acc.account_id !== currentAccountId);
      setAccounts(otherAccounts);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError(t('copyCampaign.loadAccountsFailed'));
    }
  };

  const loadCampaigns = async () => {
    if (!sourceAccountId) return;

    setLoadingCampaigns(true);
    setError(null);

    try {
      let loadedCampaigns: CampaignItem[] = [];

      if (type === 'mass') {
        const campaignsList = await api.getMassMessageCampaigns(sourceAccountId);
        loadedCampaigns = campaignsList.map(campaign => ({
          id: campaign.id,
          name: campaign.name,
          is_active: campaign.is_active,
        }));
      } else if (type === 'priority') {
        const campaignsList = await api.getPriorityMassMessageCampaigns(sourceAccountId);
        loadedCampaigns = campaignsList.map(campaign => ({
          id: campaign.id,
          name: campaign.name,
          is_active: campaign.is_active,
        }));
      }

      setCampaigns(loadedCampaigns);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setError(t('copyCampaign.loadCampaignsFailed'));
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleToggleCampaign = (id: number) => {
    const newSelection = new Set(selectedCampaigns);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedCampaigns(newSelection);
  };

  const handleSelectAll = () => {
    const allIds = campaigns.map(campaign => campaign.id);
    setSelectedCampaigns(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedCampaigns(new Set());
  };

  const handleCopy = async () => {
    if (!sourceAccountId || selectedCampaigns.size === 0) {
      setError(t('copyCampaign.selectSourceAndCampaign'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const campaignIds = Array.from(selectedCampaigns);
      
      let result;
      if (type === 'mass') {
        result = await api.copyMassMessageCampaigns(currentAccountId, sourceAccountId, campaignIds);
      } else {
        result = await api.copyPriorityMassMessageCampaigns(currentAccountId, sourceAccountId, campaignIds);
      }

      setCopyResult({
        campaigns: result.copied_campaigns,
        lists: result.copied_lists,
        messages: result.copied_messages,
      });
      setSuccess(true);
      
      setTimeout(() => {
        onComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Failed to copy campaigns:', err);
      setError(err.message || t('copyCampaign.copyCampaignsFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{getTitle()}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {success ? (
            <div className={styles.successMessage}>
              <p>{t('copyCampaign.successCopied')}</p>
              {copyResult && (
                <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                  <div>{t('copyCampaign.campaigns', { count: copyResult.campaigns })}</div>
                  <div>{t('copyCampaign.lists', { count: copyResult.lists })}</div>
                  <div>{t('copyCampaign.messages', { count: copyResult.messages })}</div>
                </div>
              )}
              <p style={{ marginTop: '8px' }}>{t('copyCampaign.refreshing')}</p>
            </div>
          ) : (
            <>
              <div className={styles.section}>
                <Select
                  placeholder={t('copyCampaign.selectAnAccount')}
                  selectedKey={sourceAccountId || null}
                  onSelectionChange={(key: Key | null) => {
                    setSourceAccountId(key ? String(key) : '');
                    setSelectedCampaigns(new Set());
                  }}
                  isDisabled={loading}
                  className="w-full"
                >
                  <Label className="block font-medium mb-2 text-[var(--text-secondary)] text-sm">{t('copyCampaign.sourceAccount')}</Label>
                  <Select.Trigger className="w-full px-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] hover:border-[var(--accent-primary)] transition-all duration-300">
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg shadow-xl z-[1100]">
                    <ListBox className="p-1 max-h-60 overflow-auto">
                      {accounts.map(acc => (
                        <ListBox.Item
                          key={acc.account_id}
                          id={acc.account_id}
                          textValue={acc.profile_name || acc.username || acc.account_id}
                          className="px-3 py-2 rounded-md text-sm text-[var(--text-primary)] cursor-pointer hover:bg-[var(--bg-hover)] data-[selected]:bg-[var(--accent-primary)]/10 data-[selected]:text-[var(--accent-primary)]"
                        >
                          {acc.profile_name || acc.username || acc.account_id}
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </div>

              {sourceAccountId && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <label>{t('copyCampaign.selectCampaignsToCopy')}</label>
                    <div className={styles.selectButtons}>
                      <button onClick={handleSelectAll} className={styles.textButton}>
                        {t('copyCampaign.selectAll')}
                      </button>
                      <button onClick={handleDeselectAll} className={styles.textButton}>
                        {t('copyCampaign.deselectAll')}
                      </button>
                    </div>
                  </div>

                  {loadingCampaigns ? (
                    <LoadingBlock label={t('copyCampaign.loadingCampaigns')} />
                  ) : campaigns.length === 0 ? (
                    <div className={styles.emptyState}>
                      {t('copyCampaign.noCampaignsAvailable')}
                    </div>
                  ) : (
                    <div className={styles.itemsList}>
                      {campaigns.map((campaign) => (
                        <div key={campaign.id} className={styles.checkboxItem}>
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedCampaigns.has(campaign.id)}
                              onChange={() => handleToggleCampaign(campaign.id)}
                              disabled={loading}
                            />
                            <span className={styles.itemName}>
                              {campaign.name}
                              {!campaign.is_active && ` ${t('copyCampaign.inactive')}`}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className={styles.errorMessage}>{error}</div>
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            onClick={onClose}
            className={styles.cancelButton}
            disabled={loading}
          >
            {t('copyCampaign.cancel')}
          </button>
          <button
            onClick={handleCopy}
            className={styles.copyButton}
            disabled={loading || !sourceAccountId || selectedCampaigns.size === 0 || success}
          >
            {loading ? t('copyCampaign.copying') : t('copyCampaign.copyCampaigns', { count: selectedCampaigns.size })}
          </button>
        </div>
      </div>
    </div>
  );
}
